import type {
  LoaderFunctionArgs,
  ActionFunctionArgs,
  MetaFunction,
} from "@remix-run/node";
import { redirect } from "@remix-run/node";
import { useLoaderData, useActionData } from "@remix-run/react";
import ProgressStepper from "~/components/ProgressStepper";
import {
  commitSignupSession,
  destroySignupSession,
  getSignupSession,
} from "~/services/signupSession.server";
import Names from "~/components/signupSections/components/Names";
import EmailPassword from "~/components/signupSections/components/EmailPassword";
import type { SignUpUserPayload } from "~/models/auth";
import { commitSession, getSession } from "~/services/auth.server";
import { defaultMetaTagsBuilder } from "~/utils/meta";
import GenderDob from "~/components/signupSections/components/GenderDob";
import {
  isEmailAvailable,
  isPasswordValid,
  isUsernameAvailable,
  signupUser,
} from "~/services/signup.server";

export const meta: MetaFunction = ({ location }) => {
  const url = new URL(location.pathname, "https://app.7qz.io");
  return defaultMetaTagsBuilder({
    title: "Sign-Up",
    description: "Create your 7Qz account",
    url: url,
  });
};

export async function loader({ request, params }: LoaderFunctionArgs) {
  const session = await getSignupSession(request.headers.get("Cookie"));
  const section = params.section;

  const incompleteScreens = session.get("incompleteScreens") || [];
  const flow = session.get("flow") || [];

  const incompleteScreensData: Array<string> = [...incompleteScreens];
  const flowData: Array<string> = [...flow];

  // if the session does not have the section contained within the incompleteScreens array that means the user has already completed that section
  if (!incompleteScreens.includes(section)) {
    return redirect("/signup");
  }

  return {
    section,
    incompleteScreens: incompleteScreensData,
    flow: flowData,
  };
}

export default function RegionalSignupFlow() {
  const actionData = useActionData<typeof action>();
  const { section, flow } = useLoaderData<typeof loader>();

  return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-background p-6 md:p-10">
        <ProgressStepper
          // steps={flow.length}
            steps={3}
          // currentStep={flow.indexOf(section ?? "") + 1}
            currentStep={1}
        />
        {section === "names" && (
            <Names
                errorMessage={actionData?.errorMessage}
                errorFields={actionData?.fields}
                defaultValues={
                  actionData?.values as {
                    firstName: string;
                    lastName: string;
                    userName: string;
                  }
                }
            />
        )}
        {section === "email-password" && (
            <EmailPassword
                errorMessage={actionData?.errorMessage}
                errorFields={actionData?.fields}
                defaultValues={
                  actionData?.values as {
                    email: string;
                    password: string;
                    confirmPassword: string;
                  }
                }
            />
        )}
        {section === "genderDob" && <GenderDob />}
      </div>
  );
}

export async function action({ request, params }: ActionFunctionArgs) {
  const url = new URL(request.url);
  const redirectQuery = new URLSearchParams(url.search).get("redirect");

  const section = params.section;

  if (!section) {
    return redirect("/login");
  }

  const formData = await request.formData();
  const formDataContent = Object.fromEntries(formData.entries());

  console.log('formData: ', formData)
  console.log('formDataContent: ', formDataContent)

  const session = await getSignupSession(request.headers.get("Cookie"));

  if (formDataContent._action === "submit-names") {
    const validationResult = await isUsernameAvailable(
      formDataContent.userName as string
    );

    if (!validationResult.success) {
      return {
        errorMessage: validationResult.error?.message,
        fields: ["userName"],
        values: {
          firstName: formDataContent.firstName,
          lastName: formDataContent.lastName,
          userName: formDataContent.userName,
        },
      };
    }

    session.set(section, {
      firstName: formDataContent.firstName,
      lastName: formDataContent.lastName,
      userName: formDataContent.userName,
    });
  } else if (formDataContent._action === "submit-emailPassword") {
    const validateEmailResult = await isEmailAvailable(
      formDataContent.email as string
    );

    if (!validateEmailResult.success) {
      return {
        errorMessage: validateEmailResult.error?.message,
        fields: ["email"],
        values: {
          email: formDataContent.email,
          password: formDataContent.password,
          confirmPassword: formDataContent.confirmPassword,
        },
      };
    }

    const validatePasswordResult = isPasswordValid(
      formDataContent.password as string,
      formDataContent.confirmPassword as string
    );

    if (!validatePasswordResult.success) {
      return {
        errorMessage: validatePasswordResult.error?.message,
        fields: ["password"],
        values: {
          email: formDataContent.email,
          password: formDataContent.password,
          confirmPassword: formDataContent.confirmPassword,
        },
      };
    }

    session.set(section, {
      email: formDataContent.email,
      password: formDataContent.password,
    });
  } else if (formDataContent._action === "submit-genderDob") {
    function getDoubleDigitNumber(number: number) {
      return number < 10 ? `0${number}` : number;
    }

    const dobString = `${getDoubleDigitNumber(
      +formDataContent.year
    ).toString()}-${getDoubleDigitNumber(
      +formDataContent.month
    ).toString()}-${getDoubleDigitNumber(
      +formDataContent.day
    ).toString()}T00:00:00.000Z`;

    const genderString = formDataContent.gender as string;

    const updatedGen = genderString.replaceAll(/\s/g, "_").toUpperCase();

    session.set(section, {
      dob: dobString,
      gender: updatedGen,
    });
  }

  const incompleteScreens = session.get("incompleteScreens") || [];
  const updatedIncompleteScreens = incompleteScreens.filter(
    (screen: string) => screen !== section
  );
  session.set("incompleteScreens", updatedIncompleteScreens);

  if (updatedIncompleteScreens.length === 0) {
    // we have all we need to complete the signup in the session storage

    const screens = session.get("flow");
    let signUpUserPayload: SignUpUserPayload = {
      firstName: "",
      lastName: "",
      username: "",
      email: "",
      password: "",
      dob: "",
      gender: "",
      role: 'USER'
    };
    for (const screen of screens) {
      const screenData = session.get(screen);
      signUpUserPayload = Object.assign(signUpUserPayload, screenData);
    }

    const signUpUserResponse = await signupUser(signUpUserPayload);

    if (!signUpUserResponse.success) {
      return redirect("/");
    }

    const authSession = await getSession(request.headers.get("Cookie"));
    authSession.set("user", {...signUpUserPayload, id: signUpUserResponse.payload?.id} );
    authSession.set("strategy", "email-password");

    return redirect(redirectQuery ? redirectQuery : "/", {
      headers: [
        [
          "Set-Cookie",
          await commitSession(authSession, {
            expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 7),
          }),
        ],
        // remove the signup session cookie
        ["Set-Cookie", await destroySignupSession(session)],
      ],
    });
  } else {
    return redirect(
      redirectQuery
        ? `/signup/${updatedIncompleteScreens[0]}?redirect=${redirectQuery}`
        : `/signup/${updatedIncompleteScreens[0]}`,
      {
        headers: {
          "Set-Cookie": await commitSignupSession(session),
        },
      }
    );
  }
}
